<template>
  <van-action-sheet
    :title="t('message.ai.recommendation')"
    v-bind="$attrs"
    class="h-70vh! [&_.van-action-sheet\_\_content]:overflow-hidden! select-none"
    v-model:show="show"
    :teleport="body"
  >
    <van-tabs
      :active="active"
      @click-tab="(item) => handleTabClick(item.name)"
      swipeable
      class="flex flex-col h-full [&_.van-tab]:flex-auto! [&_.van-tab\_\_text]:font-600 [&_.van-tabs=\_\_wrap]:overflow-visible! [&_.van-tabs\_\_content]:flex-1 [&_.van-tab\_\_panel]:h-full! [&_.van-tab\_\_panel]:overflow-auto!"
    >
      <van-tab v-for="(item, index) in goodsData" :key="index" :name="index">
        <template #title>
          <div class="flex items-center gap-4 flex-nowrap">
            {{ item.title }}
            <img
              v-if="item.sortStrategy.includes('PRICE')"
              :src="
                item.sortStrategy === 'ITEM_PRICE_DESC' ? PriceDown : PriceUp
              "
              class="w-6 h-11"
            />
          </div>
        </template>
        <van-pull-refresh v-model="item.refreshing" @refresh="onRefresh">
          <van-list
            v-model:loading="item.loading"
            :finished="item.finished"
            @load="getGoodsData"
            :immediate-check="false"
          >
            <GoodsList
              :goods-data="item.goodsList"
              class="min-h-100"
              :source="GoodsSource.Recommend"
            />
          </van-list>
        </van-pull-refresh>
      </van-tab>
    </van-tabs>
  </van-action-sheet>
</template>

<script setup lang="ts">
import { nextTick, ref, watch } from "vue";
import GoodsList from "./GoodsList.vue";
import { Message, SortStrategy } from "./type";
import { getRecommendGoods } from "@/api/aiChat";
import { useSetting } from "@/composables/use-setting";
import PriceDown from "@/assets/images/ai-chat/price-down.png";
import PriceUp from "@/assets/images/ai-chat/price-up.png";
import { useI18n } from "vue-i18n";
import { GoodsSource } from "./utils/goods-source";
const props = defineProps<{
  message: Message;
}>();

const { t } = useI18n();

const show = defineModel<boolean>("show");

interface TabGoodsData {
  title: string;
  loading: boolean;
  goodsList: any[];
  pageNow: number;
  finished: boolean;
  refreshing: boolean;
  sortStrategy: SortStrategy;
  isPrice?: boolean;
}

const body = document.body;

const goodsData = ref<TabGoodsData[]>([
  {
    title: t("message.ai.mostPopular"),
    loading: false,
    goodsList: [],
    pageNow: 0,
    finished: false,
    refreshing: false,
    sortStrategy: SortStrategy.DefaultScore,
  },
  {
    title: t("message.ai.TopSales"),
    loading: false,
    goodsList: [],
    pageNow: 0,
    finished: false,
    refreshing: false,
    sortStrategy: SortStrategy.SaleCount,
  },
  {
    title: t("message.ai.Price"),
    loading: false,
    goodsList: [],
    pageNow: 0,
    finished: false,
    refreshing: false,
    sortStrategy: SortStrategy.ItemPriceDesc,
    isPrice: true,
  },
]);

const active = ref(0);

const { getClientInfo } = useSetting();
const clientInfo = getClientInfo();

const getGoodsData = async () => {
  await nextTick();
  const index = active.value;
  const tab = goodsData.value[index];
  tab.pageNow++;
  tab.loading = true;
  const res = await getRecommendGoods({
    deviceCode: clientInfo.deviceCode,
    sessionId: props.message.sessionId!,
    requestId: props.message.requestId!,
    stageId: props.message.stageId!,
    pageIndex: tab.pageNow,
    pageSize: 10,
    /**
     * 排序方式：
     * DEFAULT_SCORE：默认算法分数
     * SALE_COUNT：销量
     * ITEM_PRICE_DESC：价格降序
     * ITEM_PRICE_ASC：价格升序
     */
    sortStrategy: tab.sortStrategy,
  });

  tab.loading = false;
  const json = await res.json();

  if (tab.refreshing) {
    tab.goodsList = [];
    tab.refreshing = false;
  }
  if (json.totalCount <= tab.goodsList.length) {
    tab.finished = true;
    return;
  } else {
    tab.goodsList = [...tab.goodsList, ...(json.itemList || [])];
  }
};

const onRefresh = () => {
  const index = active.value;
  const tab = goodsData.value[index];
  tab.finished = false;
  tab.pageNow = 0;
  tab.refreshing = true;
  getGoodsData();
};

const handleTabClick = (name: number) => {
  const tab = goodsData.value[name];
  const isPrice = name === goodsData.value.length - 1;
  if (isPrice) {
    tab.sortStrategy =
      tab.sortStrategy === SortStrategy.ItemPriceDesc
        ? SortStrategy.ItemPriceAsc
        : SortStrategy.ItemPriceDesc;
  }
  if (!tab.goodsList.length || (active.value === name && isPrice)) {
    active.value = name;
    onRefresh();
  } else {
    active.value = name;
  }
};

watch([show, goodsData], ([val1, val2]) => {
  if (val1 && !val2[0].goodsList.length) {
    onRefresh();
  }
});
</script>
